import {
  ClientsSources,
  ClientsSourcesTranslate,
  OrganizationTypes,
  OrganizationTypesTranslate,
} from '@/enums';
import { BaseApiService } from '@/services/base-service';
import {
  ApiPagination,
  BaseResponse,
  RequestPayload,
  Response,
} from '@/types';
import { del_mask } from '@/utils';
import { Clients } from '@/modules/clients/types';

interface ClientsList extends BaseResponse, ApiPagination {
  data: {
    items: Clients.Client[],
    cities: string[],
  }
}

export class ClientsService extends BaseApiService {
  private readonly base_path = 'partner';

  async get_clients(payload: RequestPayload<Clients.RequestFilters>): Promise<Clients.Response> {
    const url = `${this.base_path}/clients`;
    const payload_filters = payload.filters;

    payload_filters.search = payload_filters.search ? payload_filters.search : '';

    const { data } = await this.http_client.get<Clients.Response>(url, {
      params: {
        filters: payload_filters,
        filters_exist: payload.filters_exist,
        page: payload.pagination.page,
        limit: payload.pagination.limit,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async add_client(client: Clients.ClientStoreRequest): Promise<Response<Record<'client_id', number>>> {
    const url = `${this.base_path}/clients`;
    const payload = client;

    payload.kpp_client = del_mask(payload.kpp_client);
    payload.inn_client = del_mask(payload.inn_client);
    payload.client_contact.telephone = del_mask(payload.client_contact.telephone);
    const { data } = await this.http_client.post<Response<Record<'client_id', number>>>(url, client);

    return data;
  }

  async update_client(client: Clients.Client): Promise<BaseResponse> {
    const url = `${this.base_path}/clients/${client.id}`;
    const payload = client;

    if (typeof payload.client_source_type === 'object') {
      payload.client_source_type = payload.client_source_type?.slug || ClientsSources.CREATE;
    }

    if (typeof payload.organization_type === 'object') {
      payload.organization_type = payload.organization_type?.slug || OrganizationTypes.ORGANIZATION;
    }

    payload.kpp_client = del_mask(payload.kpp_client);
    payload.inn_client = del_mask(payload.inn_client);

    const { data } = await this.http_client.patch<BaseResponse>(url, payload);

    return data;
  }

  async get_client_by_id(id: string | number): Promise<Response<Clients.Client>> {
    const url = `${this.base_path}/clients/${id}`;
    const { data } = await this.http_client.get<Response<Clients.Client>>(url);

    return data;
  }

  async active_client(id: string | number, is_active: boolean): Promise<Response<Clients.Client>> {
    const url = `${this.base_path}/clients/${id}/active`;
    const payload = {
      is_active,
    };

    const { data } = await this.http_client.patch<Response<Clients.Client>>(url, payload);

    return data;
  }

  async get_clients_to_add(filters: Clients.ClientsToAddRequest): Promise<Clients.ClientsToAddResponse> {
    const url = `${this.base_path}/clients_to_add`;
    const payload = filters;

    const { data } = await this.http_client.get<Clients.ClientsToAddResponse>(url, {
      params: {
        filters: payload.filters,
        page: payload.pagination.page,
        limit: payload.pagination.limit,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }
}

export const default_client = (): Clients.Client => ({
  name_client_company: '',
  name_client_organization: '',
  inn_client: '',
  kpp_client: '',
  city: '',
  region: '',
  address: '',
  organization_type: {
    slug: OrganizationTypes.ORGANIZATION,
    name: OrganizationTypesTranslate[OrganizationTypes.ORGANIZATION],
  },
  client_source_type: {
    slug: ClientsSources.CREATE,
    name: ClientsSourcesTranslate[ClientsSources.CREATE],
  },
  created_at: '',
  is_resident: true,
});
